import {
  Users,
  Target,
  Eye,
  Award,
  Lightbulb,
  Shield,
  Users2,
  TrendingUp,
  Mail,
  Phone,
  Globe,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import executiveMeeting from "@/assets/executive-meeting.jpg";
import profMartin from "/leaders/prof-martin.jpg";
import leadershipPresentation from "@/assets/leadership-presentation.jpg";

const About = () => {
  const coreValues = [
    {
      icon: Award,
      title: "Excellence",
      description:
        "Commitment to the highest standards in all our training programs",
    },
    {
      icon: Lightbulb,
      title: "Innovation",
      description:
        "Continuous improvement and adoption of modern training methodologies",
    },
    {
      icon: Shield,
      title: "Integrity",
      description: "Ethical conduct and transparency in all business dealings",
    },
    {
      icon: Users2,
      title: "Inclusivity",
      description:
        "Ensuring equal access to quality training for all participants",
    },
    {
      icon: TrendingUp,
      title: "Impact",
      description:
        "Measurable outcomes that drive organizational and individual success",
    },
  ];
  const journey = [
    {
      year: "Apr 2025",
      title: "Incorporation",
      description:
        "TCLA-X was incorporated in Kenya as a private limited company in February 2025, with a focus on transformative leadership and capacity building across Africa.",
    },
    {
      year: "May 2025",
      title: "Managing Director Appointment",
      description:
        "Prof. Martin Njoroge is the current Founding Director, bringing a strong background in digital innovation, leadership development, and youth empowerment, as well as wealth of knowledge in transforming individual and organizational performance.",
    },
    {
      year: "July 2025",
      title: "Company Details",
      description:
        "Registered under Kenyan law with  Reg. No: P052445464L, TCLA-X operates in full compliance with the Companies Act, and is headquartered in Nairobi, Kenya.",
    },
    {
      year: "July 2025",
      title: "Flagship Program Launch",
      description:
        "Currently finalizing on its first leadership training program, co-designed with experts and focused on adaptive, high-impact leadership for emerging professionals.",
    },
  ];

  const [currentImage, setCurrentImage] = useState(0);
  const backgroundImages = [executiveMeeting];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % backgroundImages.length);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Layout>
    <section
      className="py-6 md:py-32 relative"
      id="about"
      style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.5)), url(${executiveMeeting})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-4">
        <div className="flex flex-col lg:flex-row gap-8 items-center">
          {/* Image/Logo - Left */}
          <div className="w-full flex justify-center lg:justify-start lg:w-2/5 py-2">
            <div className="w-[50%] sm:w-[60%] md:w-[250px] aspect-square bg-gray-100 rounded-lg overflow-hidden shadow-lg">
              <img
                src="/images/logo.jpg"
                alt="TCLA-X Story"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
  
          {/* Text Content - Right */}
          <div className="w-full lg:px-10">
            <div className="text-center mb-6 lg:mb-10">
              <h1 className="font-display font-bold text-xl md:text-5xl lg:text-6xl mb-6 text-white">
                Our <span className="text-blue-400">TCLA-X</span> Story
              </h1>
            </div>
            <div className="space-y-2  md:text-left px-1">
              <p className="text-gray-200 text-lg md:text-xl leading-relaxed text-center">
                <span className="font-semibold text-blue-400 text-md">TCLA-X </span> 
                 represents more than training – it's a commitment to
                excellence that transforms lives.
              </p>
              <p className="text-gray-200 text-sm md:text-lg leading-relaxed">
                Founded on the principles of Talent Development, Character
                Building, Life Skills Enhancement, and Academic Excellence.
                We have established ourselves as Kenya's premier capacity
                building organization, with a proven track record of impactful
                transformation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

      {/* Vision & Mission */}
      <section className="py-8 lg:py-10 bg-gradient-secondary">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <Card className="shadow-subtle">
              <CardContent className="p-8 space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center">
                    <Eye className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">
                    Vision Statement
                  </h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  To be the leading capacity building organization in Africa,
                  recognized for delivering world-class training solutions that
                  transform lives and organizations.
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-subtle">
              <CardContent className="p-8 space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">
                    Mission Statement
                  </h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  To empower individuals and organizations to achieve
                  sustainable excellence through innovative training solutions,
                  strategic coaching, and transformative mentorship programs
                  that drive productivity, efficiency, and job satisfaction.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      {/* Our Journey */}
      <section
        className="py-16 md:py-24 relative"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.6)), url(${leadershipPresentation})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-display font-semibold text-3xl md:text-4xl lg:text-5xl mb-6 text-white">
              Our <span className="text-primary-glow">Journey</span>
            </h2>
            <p className="text-large text-white/90 max-w-3xl mx-auto">
              1 Year of transforming leaders and organizations across Africa
              through innovation, excellence, and measurable impact.
            </p>
          </div>

          <div className="space-y-8">
            {journey.map((milestone, index) => {
              const isOdd = index % 2 !== 0;

              return (
                <div key={index} className="relative">
                  {index < journey.length - 1 && (
                    <div className="absolute left-6 top-16 w-px h-16 bg-primary/30 hidden md:block"></div>
                  )}

                  <div className="grid md:grid-cols-12 gap-8 items-start">
                    <div className="md:col-span-2">
                      <div className="w-18 h-18 rounded-full bg-gradient-primary flex items-center justify-center mb-4">
                        <span className="text-primary-foreground font-medium text-sm">
                          {milestone.year}
                        </span>
                      </div>
                    </div>

                    <div className="md:col-span-10">
                      <div
                        className={`card-elegant backdrop-blur-sm ${
                          isOdd ? "bg-blue-100" : "bg-white/95"
                        }`}
                      >
                        <h3
                          className={`font-display font-medium text-xl md:text-2xl mb-3 ${
                            isOdd ? "text-yellow-600" : ""
                          }`}
                        >
                          {milestone.title}
                        </h3>
                        <p className="text-muted-foreground font-small leading-relaxed">
                          {milestone.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      {/* Core Values */}
      <section className="py-8 lg:py-10 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
              Our Core Values
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our values guide every decision we make and every program we
              deliver.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {coreValues.map((value, index) => (
              <Card
                key={index}
                className="text-center shadow-subtle hover:shadow-elegant transition-smooth"
              >
                <CardContent className="p-6 space-y-4">
                  <div className="w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center mx-auto">
                    <value.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">
                    {value.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-8 lg:py-10 bg-gradient-secondary">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl lg:text-2xl font-bold text-foreground">
              Leadership Team
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Meet the experienced professionals leading TCLA-X's mission to
              transform organizations across Africa.
              <Link to="/leaders" className="text-primary hover:underline ml-2">
                View All Faculty →
              </Link>
            </p>
          </div>

          <div className="max-w-5xl mx-auto p-4">
            <Card className="shadow-elegant bg-blue-700/20">
              <CardContent className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
                  <div className="md:col-span-1">
                    <div className="relative w-48 h-48 mx-auto mb-6 rounded-50 bg-gradient-to-br from-blue-400 via-white to-purple-300 shadow-[0_4px_30px_rgba(0,0,0,0.1)] flex items-center justify-center">
                      {/* Star accents (static) */}
                      <div className="absolute top-2 left-4 w-3 h-3 bg-white rounded-full shadow-sm" />
                      <div className="absolute bottom-3 right-6 w-2 h-2 bg-yellow-200 rounded-full shadow-sm" />
                      <div className="absolute top-6 right-6 w-2.5 h-2.5 bg-white rounded-full shadow" />

                      {/* Profile Image */}
                      <img
                        src={profMartin}
                        alt="Prof Martin"
                        className="w-44 h-44 object-cover rounded-full border-4 border-white shadow-xl"
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2 space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold text-foreground">
                        Professor Martin C. Njoroge
                      </h3>
                      <p className="text-lg text-primary font-medium">
                        Managing Director
                      </p>
                    </div>

                    <p className="text-muted-foreground leading-relaxed">
                      Leading TCLA-X with extensive experience in capacity
                      building and organizational development, Professor Njoroge
                      brings a wealth of knowledge in transforming individual
                      and organizational performance.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex items-center space-x-2">
                        <Phone className="w-4 h-4 text-primary" />
                        <span className="text-sm">+254722319499</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Mail className="w-4 h-4 text-primary" />
                        <span className="text-sm">director@tcla-x.com</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Globe className="w-4 h-4 text-primary" />
                        <span className="text-sm">
                          <a href="http://www.profmartinthecoach.com/">
                            www.profmartinthecoach.com
                          </a>
                        </span>
                      </div>
                    </div>

                    <div className="mt-6">
                      <Link to="/leaders/prof-martin-njoroge">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="hover-scale"
                        >
                          View Full Profile
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          {/* Placeholder for Additional Trainers */}
          <div className="text-center py-8 bg-white rounded-lg shadow-sm border-2 border-dashed border-blue-200 max-w-4xl mx-auto p-12">
            <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-600 mb-2">
              More Distinguished Faculty
            </h3>
            <p className="text-gray-500 mb-6">
              Discover our complete team of expert trainers and their specialized areas of expertise.
            </p>
            <Link to="/leaders">
              <Button variant="outline" className="hover-scale">
                View All Faculty Profiles
              </Button>
            </Link>
          </div>
          {/* <div className="text-center mt-8">
              <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> We need to add more trainers' profiles here with photos, bios, and expertise areas.
                </p>
              </div>
            </div> */}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 lg:py-10 bg-gradient-primary text-white">
        <div className="container mx-auto px-4 text-center space-y-8">
          <div className="space-y-4">
            <h2 className="text-3xl lg:text-2xl font-bold">
              Let’s Shape the Future Together
            </h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              Partner with TCLA-X to unlock potential, accelerate growth, and
              inspire lasting organizational change.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="secondary" size="lg" asChild>
                <Link to="/contact">Connect with Us</Link>
              </Button>
              <Button
                variant="secondary"
                size="lg"
                className="border-white text-black hover:bg-white hover:text-primary"
                asChild
              >
                <Link to="/services">Our Services</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
